import type { RouteRecordRaw } from 'vue-router'
import { Permission } from '@jiaofa/constants'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import OperationDashboard from '@/views/operation/Dashboard.vue'
import OperationPage from '@/views/operation/Page.vue'

const operationRoute = (path: string, name: string, title: string, designKey: string, permissions?: string[]): RouteRecordRaw => ({
  path,
  name,
  component: OperationPage,
  meta: { title, designKey, permissions },
})

// 为常用路由添加预加载配置
export const preloadRoutes = ['Orders', 'Products', 'Stores', 'Members', 'DataOverview']

export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', hidden: true },
  },
  {
    path: '/',
    component: DefaultLayout,
    redirect: '/dashboard',
    meta: { requiresAuth: true },
    children: [
      { path: 'dashboard', name: 'Dashboard', component: OperationDashboard, meta: { title: '总部运营工作台', icon: 'DataAnalysis', permissions: [Permission.DashboardView] } },
      operationRoute('orders', 'Orders', '订单中心', 'orderCenter', [Permission.OrderView]),
      operationRoute('refunds', 'Refunds', '退款处理', 'refunds', [Permission.RefundView]),
      operationRoute('complaints', 'Complaints', '投诉建议', 'complaints', [Permission.OrderView]),
      operationRoute('stores', 'Stores', '门店列表', 'stores', [Permission.StoreView]),
      operationRoute('stores/printers', 'Printers', '打印设备', 'printers', [Permission.StoreView]),
      operationRoute('products/categories', 'ProductCategories', '商品分类', 'productCategories', [Permission.ProductView]),
      operationRoute('products', 'Products', '商品列表', 'products', [Permission.ProductView]),
      operationRoute('products/combos', 'ProductCombos', '商品套餐', 'productCombos', [Permission.ProductView]),
      operationRoute('products/extras', 'ProductExtras', '加料管理', 'productExtras', [Permission.ProductView]),
      operationRoute('products/inventory', 'Inventory', '库存管理', 'inventory', [Permission.ProductView]),
      operationRoute('delivery/pricing', 'ProductPricing', '商品价格', 'productPricing', [Permission.DeliveryView]),
      operationRoute('delivery/rules', 'DeliveryRules', '配送规则', 'deliveryRules', [Permission.DeliveryView]),
      operationRoute('delivery/min-amount', 'DeliveryMinAmount', '起送金额', 'deliveryMinAmount', [Permission.DeliveryView]),
      operationRoute('delivery/areas', 'DeliveryAreas', '门店配送范围', 'deliveryAreas', [Permission.DeliveryView]),
      operationRoute('delivery/points', 'DeliveryPoints', '配送点管理', 'deliveryPoints', [Permission.DeliveryView]),
      operationRoute('drones/drones', 'DroneDevices', '无人机设备', 'droneDevices', [Permission.DroneView]),
      operationRoute('drones/tasks', 'DroneTasks', '任务调度', 'droneTasks', [Permission.DroneView]),
      operationRoute('drones/flights', 'DroneFlights', '飞行记录', 'droneFlights', [Permission.DroneView]),
      operationRoute('drones/faults', 'DroneFaults', '故障管理', 'droneFaults', [Permission.DroneView]),
      operationRoute('drones/maintenances', 'DroneMaintenances', '维护管理', 'droneMaintenances', [Permission.DroneView]),
      operationRoute('users', 'Users', '用户管理', 'userManagement', [Permission.UserView]),
      operationRoute('members/levels', 'MemberLevels', '会员等级', 'memberLevels', [Permission.MemberView]),
      operationRoute('members/rules', 'MemberRules', '规则配置', 'memberRules', [Permission.MemberView]),
      {
        path: 'members/benefits',
        name: 'MemberBenefits',
        component: OperationPage,
        meta: { title: '会员权益', designKey: 'memberBenefits', permissions: [Permission.MemberView], hidden: true },
      },
      operationRoute('marketing/coupons', 'Coupons', '优惠券', 'marketingCoupons', [Permission.MarketingView]),
      operationRoute('marketing/points-mall', 'PointsMall', '积分商城', 'marketingPointsMall', [Permission.MarketingView]),
      operationRoute('marketing/cards', 'MarketingCards', '周卡月卡', 'marketingCards', [Permission.MarketingView]),
      operationRoute('marketing/recharge', 'RechargePackages', '充值套餐', 'marketingRecharge', [Permission.MarketingView]),
      operationRoute('marketing/banners', 'MarketingBanners', '首页 Banner', 'marketingBanners', [Permission.MarketingView]),
      operationRoute('marketing/group-buy', 'GroupBuy', '拼团活动', 'groupBuy', [Permission.MarketingView]),
      operationRoute('marketing/bargain', 'MarketingBargain', '砍价活动', 'marketingBargain', [Permission.MarketingView]),
      operationRoute('marketing/invite', 'MarketingInvite', '邀请有礼', 'marketingInvite', [Permission.MarketingView]),
      operationRoute('marketing/flash-sale', 'FlashSale', '限时秒杀', 'flashSale', [Permission.MarketingView]),
      operationRoute('data/overview', 'DataOverview', '经营概览', 'dataOverview', [Permission.DataView]),
      operationRoute('data/orders', 'DataOrders', '订单分析', 'dataOrders', [Permission.DataView]),
      operationRoute('data/products', 'DataProducts', '商品分析', 'dataProducts', [Permission.DataView]),
      operationRoute('data/store-rank', 'StoreRank', '门店排行', 'storeRankPage', [Permission.DataView]),
      operationRoute('data/users', 'DataUsers', '用户分析', 'dataUsers', [Permission.DataView]),
      operationRoute('finance/transactions', 'Transactions', '交易流水', 'transactions', [Permission.FinanceView]),
      operationRoute('finance/reconciliation', 'Reconciliation', '门店对账', 'reconciliation', [Permission.FinanceView]),
      operationRoute('finance/refunds', 'RefundTransactions', '退款流水', 'refundTransactions', [Permission.FinanceView]),
      operationRoute('finance/payment-config', 'PaymentConfig', '支付配置', 'paymentConfig', [Permission.FinanceView]),
      operationRoute('finance/profit-sharing', 'ProfitSharing', '微信分账', 'profitSharing', [Permission.ProfitSharingView]),
      operationRoute('finance/profit-sharing/rules', 'ProfitSharingRules', '分账规则', 'profitSharingRules', [Permission.ProfitSharingView]),
      operationRoute('finance/profit-sharing/receivers', 'ProfitSharingReceivers', '分账接收方', 'profitSharingReceivers', [Permission.ProfitSharingView]),
      operationRoute('messages', 'MessageLogs', '消息记录', 'messageLogs', [Permission.MessageView]),
      operationRoute('messages/templates', 'MessageTemplates', '通知模板', 'messageTemplates', [Permission.MessageView]),
      operationRoute('messages/config', 'MessageConfig', '推送配置', 'messageConfig', [Permission.MessageView]),
      operationRoute('system/admins', 'SystemAdmins', '管理员', 'systemAdmins', [Permission.SystemView]),
      operationRoute('system/roles', 'Roles', '角色权限', 'roles', [Permission.SystemRoleView]),
      operationRoute('system/logs', 'OperationLogs', '操作日志', 'operationLogs', [Permission.SystemView]),
      operationRoute('system/params', 'SystemParams', '系统参数', 'systemParams', [Permission.SystemView]),
    ],
  },
  { path: '/403', name: 'Forbidden', component: () => import('@/views/errors/403.vue'), meta: { title: '无权限', hidden: true } },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: () => import('@/views/errors/404.vue'), meta: { title: '页面不存在', hidden: true } },
]
